import { customPalette, inputSurfaceDark } from '@shared/theme/StyleProvider';

/**
 * The surface every Quill editor sits on: white (never a grey fill), with the
 * text-input background in dark mode. The frame around it - border, hover and
 * focus states - comes from `RichEditorOutline`, so editors read exactly like
 * outlined inputs.
 */
export const qlEditorSurfaceStyles = {
  borderRadius: 8,
  overflow: 'hidden' as const,
  backgroundColor: 'var(--mui-palette-background-paper)',
  '.dark &': { backgroundColor: inputSurfaceDark },
};

/**
 * Opaque repaint of the editor surface, for elements that must hide the
 * content scrolling under them (sticky toolbars). The dark tint is
 * translucent, so it gets composited over paper.
 */
export const qlEditorSurfaceOpaqueBg = {
  background: 'var(--mui-palette-background-paper)',
  '.dark &': {
    background: `linear-gradient(${inputSurfaceDark}, ${inputSurfaceDark}), var(--mui-palette-background-paper)`,
  },
};

export const qlEditorBaseStyles = {
  boxSizing: 'border-box' as const,
  counterReset:
    'list-0 list-1 list-2 list-3 list-4 list-5 list-6 list-7 list-8 list-9',
  lineHeight: 1.42,
  overflow: 'auto' as const,
  tabSize: 4,
  textAlign: 'left' as const,
  whiteSpace: 'pre-wrap' as const,
  wordWrap: 'break-word' as const,
  touchAction: 'manipulation' as const,
  a: { color: customPalette.blue500 },
};

export const qlListStyles = {
  '.ql-editor ol, .ql-editor ul': { margin: 0, paddingLeft: '1.5em' },
  '.ql-editor li': {
    listStyleType: 'none',
    paddingLeft: '1.5em',
    position: 'relative' as const,
  },
  '.ql-editor li > .ql-ui:before': {
    display: 'inline-block' as const,
    marginLeft: '-1.5em',
    marginRight: '.3em',
    textAlign: 'right' as const,
    whiteSpace: 'nowrap' as const,
    width: '1.2em',
  },
  '.ql-editor li[data-list=bullet] > .ql-ui:before': {
    content: "'\\2022'",
  },
  '.ql-editor li[data-list=ordered]': {
    counterIncrement: 'list-0',
  },
  '.ql-editor li[data-list=ordered] > .ql-ui:before': {
    content: "counter(list-0, decimal) '. '",
  },
  ...Object.fromEntries(
    Array.from({ length: 9 }, (_, index) => [
      `.ql-editor .ql-indent-${index + 1}`,
      { paddingLeft: `${(index + 1) * 3}em` },
    ]),
  ),
};
